import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import AuthLayout from '../../components/layout/AuthLayout';
import { Button, Input } from '../../components/ui';
import { sendCustomPasswordReset } from '../../lib/api';

const resetSchema = z.object({
  email: z.string().email('Invalid email address'),
});

export default function ResetPassword() {
  const [submitted, setSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(resetSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = async (data) => {
    setLoading(true);
    setError(null);
    try {
      await sendCustomPasswordReset({ email: data.email });
      setSubmittedEmail(data.email);
      setSubmitted(true);
    } catch (err) {
      console.error('Failed to send password reset:', err);
      setError('Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <AuthLayout
        title="Check your email"
        showBackLink
        backLinkTo="/auth/login"
        backLinkText="Back to login"
      >
        <div className="text-center space-y-6">
          {/* Success icon */}
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-success-light">
            <svg
              className="h-8 w-8 text-success"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>

          <div>
            <p className="text-gray-600">
              We sent a password reset link to
            </p>
            <p className="font-medium text-gray-900 mt-1">
              {submittedEmail}
            </p>
          </div>

          <p className="text-sm text-gray-500">
            Click the link in your email to reset your password.
            Check your spam folder if you don't see it.
          </p>

          <Link
            to="/auth/login"
            className="inline-block"
          >
            <Button variant="secondary">
              Return to login
            </Button>
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="Enter your email and we'll send you a reset link."
      showBackLink
      backLinkTo="/auth/login"
      backLinkText="Back to login"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Error */}
        {error && (
          <div className="rounded-md bg-error-light p-4">
            <p className="text-sm text-error">{error}</p>
          </div>
        )}

        {/* Email */}
        <Input
          label="Email address"
          type="email"
          autoComplete="email"
          placeholder="john@example.com"
          error={errors.email?.message}
          dark
          {...register('email')}
        />

        {/* Submit */}
        <Button
          type="submit"
          fullWidth
          loading={loading}
        >
          Send reset link
        </Button>
      </form>
    </AuthLayout>
  );
}
